//@ts-strict-ignore
/*!
  Copyright (c) 2016 Jed Watson.
  Licensed under the MIT License (MIT), see
  http://jedwatson.github.io/react-select
*/

import React, { Component, forwardRef, ReactElement, ReactNode, useCallback, useContext, useState } from 'react';
import AutosizeInput from '../../components/AutosizeInput';
import classNames from 'classnames';

import defaultArrowRenderer from './utils/defaultArrowRenderer';
import defaultFilterOptions from './utils/defaultFilterOptions';
import defaultClearRenderer from './utils/defaultClearRenderer';

import Async from './Async';
import AsyncCreatable from './AsyncCreatable';
import Creatable from './Creatable';
import Option from './Option';
import Value from './Value';
import RenderInBody from '../../components/RenderInBody';
import _get from 'lodash/get';
import SelectModal from '../../components/SelectModal';
import { appActions } from '../../state/modules/app';
import { useDispatch } from 'react-redux';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import MenuRenderer from './utils/MenuRenderer';
import { Maybe } from '../../graphql/schema/p4types';
import { DocumentNode } from 'graphql';
import { EntityType, IDropdownOptionRenderer } from '../../@types/entityTypes';
import SidebarContext, { SidebarType } from '../Context/SidebarContext';
import titleCase from '../../utils/TitleCase';

const DROPDOWN_WIDTH = 425;
const DROPDOWN_MAX_HEIGHT = 200;
const DROPDOWN_OPTION_HEIGHT = 35;
const DROPDOWN_PADDING = 8;

function stringifyValue(value) {
  const valueType = typeof value;
  if (valueType === 'string') {
    return value;
  } else if (valueType === 'object') {
    return JSON.stringify(value);
  } else if (valueType === 'number' || valueType === 'boolean') {
    return String(value);
  }
  return '';
}

let instanceId = 1;

const SidebarIcon = ({
  value,
  sidebarForm,
  inGrid,
}: {
  value: { id: number } | Array<{ id: number }>;
  sidebarForm: SidebarType;
  inGrid: boolean;
}) => {
  const dispatch = useDispatch();
  const sidebarContext = useContext(SidebarContext);

  if (!value || !sidebarForm || inGrid) return null;

  if (Array.isArray(value)) {
    if (value?.length === 0 || value?.length > 1) {
      return null;
    }
  }

  return (
    <span className="Select-arrow-zone">
      <div
        onClick={() =>
          !sidebarContext?.openSidebar
            ? dispatch(appActions.toggleSidebar(false, value?.id || value?.[0]?.id, sidebarForm))
            : sidebarContext.openSidebar(sidebarForm, value?.id || value?.[0]?.id)
        }
        className="icon"
      >
        more_vert
      </div>
    </span>
  );
};

const OptionCounter = ({ options, totalOptionsCount, query, setWorkspaceModalOpen }) => {
  if (!(options?.length && totalOptionsCount)) return null;

  const message = query
    ? `Viewing ${options.length} of ${totalOptionsCount}, type for more or click to expand`
    : `Viewing ${options.length} of ${totalOptionsCount}, type for more.`;

  return (
    <div
      style={{
        padding: '8px 10px',
        fontSize: '14px',
        borderTop: '#DDD 1px solid',
        color: 'lightgray',
        cursor: 'pointer',
      }}
      onClick={query ? () => setWorkspaceModalOpen(true) : undefined}
    >
      {message}
    </div>
  );
};

export type ISelectProps = {
  addLabelText: string; // placeholder displayed when you want to add a label on a multi-value input
  'aria-describedby': string; // HTML ID(s) of element(s) that should be used to describe this input (for assistive tech)
  'aria-label': string; // Aria label (for assistive tech)
  'aria-labelledby': string; // HTML ID of an element that should be used as the label (for assistive tech)
  arrowRenderer: Function; // Create drop-down caret element
  autoBlur: boolean; // automatically blur the component when an option is selected
  autofocus: boolean; // autofocus the component on mount
  autosize: boolean; // whether to enable autosizing or not
  backspaceRemoves: boolean; // whether backspace removes an item if there is no text input
  backspaceToRemoveMessage: string; // Message to use for screenreaders to press backspace to remove the current item - {label} is replaced with the item label
  className: string; // className for the outer element
  clearAllText?: string; // title for the "clear" control when multi: true
  clearRenderer: Function; // create clearable x element
  clearValueText?: string; // title for the "clear" control
  clearable: boolean; // should it be possible to reset value
  deleteRemoves: boolean; // whether backspace removes an item if there is no text input
  delimiter: string; // delimiter to use to join multiple values for the hidden field value
  disabled: boolean; // whether the Select is disabled or not
  displayFields?: Object;
  escapeClearsValue: boolean; // whether escape clears the value when the menu is closed
  filterOption: (option: any, filterString: string) => boolean; // method to filter a single option (option, filterString)
  filterOptions: any; // boolean to enable default filtering or function to filter the options array ([options], filterString, [values])
  hasErrors?: boolean;
  id?: string | number; //Not certain whether this is string or number, allowing both for the moment.
  ignoreAccents: boolean; // whether to strip diacritics when filtering
  ignoreCase: boolean; // whether to perform case-insensitive filtering
  inGrid: boolean; //Nested inside a react-data-grid component.
  inputProps: any; // custom attributes for the Input
  inputRenderer: Function; // returns a custom input component
  instanceId: string; // set the components instanceId
  isLoading: boolean; // whether the Select is loading externally or not (such as options being loaded)
  joinValues: boolean; // joins multiple values into a single form field with the delimiter (legacy mode)
  labelKey: string; // path of the label value in option objects
  labelKey2: string; // path of a backup label value (In case the former is blank).
  matchPos: string; // (any|start) match the start or entire string when filtering
  matchProp: string; // (any|label|value) which option property to filter on
  menuBuffer: number; // optional buffer (in px) between the bottom of the viewport and the bottom of the menu
  menuContainerStyle: Object; // optional style to apply to the menu container
  menuRenderer: Function; // renders a custom menu with options
  menuStyle: Object; // optional style to apply to the menu
  multi: boolean; // multi-value input
  name: string; // generates a hidden <input /> tag with this field name for html forms
  noResultsText: string | Omit<ReactNode, 'undefined'>; // placeholder displayed when there are no matching search results
  onBlur: Function; // onBlur handler: function (event) {}
  onBlurResetsInput: boolean; // whether input is cleared on blur
  onChange: Function; // onChange handler: function (newValue) {}
  onClose: Function; // fires when the menu is closed
  onCloseResetsInput: boolean; // whether input is cleared when menu is closed through the arrow
  onFocus: Function; // onFocus handler: function (event) {}
  onInputChange: Function; // onInputChange handler: function (inputValue) {}
  onInputKeyDown: Function; // input keyDown handler: function (event) {}
  onMenuScrollToBottom: Function; // fires when the menu is scrolled to the bottom; can be used to paginate options
  onOpen: Function; // fires when the menu is opened
  onValueClick: Function; // onClick handler for value labels: function (value, event) {}
  openAfterFocus: boolean; // boolean to enable opening dropdown when focused
  openOnFocus: boolean; // always open options menu on focus
  optionClassName: string; // additional class(es) to apply to the <Option /> elements
  optionComponent: Function; // option component to render in dropdown
  optionRenderer: IDropdownOptionRenderer;
  options: Array<any>; // array of options
  pageSize: number; // number of entries to page when using page up/down keys
  placeholder: string | Omit<ReactNode, 'undefined'>; // field placeholder, displayed when there's no value
  readOnly: boolean;
  renderInBody?: boolean;
  remoteFilter?: boolean;
  resetValue: any; // value to use when you clear the control
  scanQuery?: (inputValue?: string, keyCode?: number) => void; //Used for barcode scanning capability (On tab/enter/etc, triggers a faster lookup query rather than waiting for menu to load).
  scrollMenuIntoView: boolean; // boolean to enable the viewport to shift so that the full menu fully visible when engaged
  searchable: boolean; // whether to enable searching feature or not
  selectModal?: ReactNode | ReactElement; //Expandable modal for lookup (E.g. 'Product Workspace').
  selectModalData?: any;
  sidebarForm?: any;
  simpleValue: boolean; // pass the value to onChange as a simple value (legacy pre 1.0 mode), defaults to false
  style: Object; // optional style to apply to the control
  tabIndex?: number;
  tabSelectsValue: boolean; // whether to treat tabbing out while focused to be value selection
  'data-testid'?: string;
  value: any; // initial field value
  valueComponent: Function; // value component to render
  valueKey: string; // path of the label value in option objects
  valueRenderer: Function; // valueRenderer: function (option) {}
  wrapperStyle: Object; // optional style to apply to the component wrapper
  rawLabel: boolean; // option to avoid converting label to title case, e.g. for allcaps fields.
  totalOptionsCount?: number; //Optional max number of options for this dropdown (Hints to Select to show an indication to the user that they can search for more.sp
  query?: DocumentNode; //(Query used for the dropdown - For displaying expanded view)
  workspaceModalOpen: boolean;
  setWorkspaceModalOpen: (newValue: boolean) => void;
  creatable: boolean;
  textEntered: boolean;
  customIcon?: (props: { selectValue: (rowData: any) => void; focusOnInput: () => void }) => ReactElement; // Custom icon to appear next to the "Spyglass" icon (Or in place of it, where appropriate).
  entity?: EntityType; // Pass-through for SelectModal::DropdownWorkspace for improved functionality.
};

type IDropdownPositionState = {
  dropdownHPos?: 'left' | 'right';
  dropdownVPos?: 'top' | 'bottom';
  dropdownTop?: number;
  dropdownLeft?: number;
  dropdownRight?: number;
};

type ISelectState = IDropdownPositionState & {
  inputValue?: string;
  isFocused: boolean;
  isOpen: boolean;
  isPseudoFocused: boolean;
  onOpen?: Function;
  onClose?: Function;
  focusedIndex?: Maybe<number>;
  focusedOption?: any;
};

class Select extends Component<ISelectProps, ISelectState> {
  displayName = 'Select';
  private readonly _instancePrefix: string;
  private readonly menuRef: React.RefObject<HTMLDivElement>;
  private readonly menuContainerRef: React.RefObject<any>;
  private readonly wrapperRef: React.RefObject<any>;
  private readonly inputRef: React.RefObject<any>;
  private focusedRef: React.MutableRefObject<Maybe<HTMLElement>>;
  private hasScrolledToOption: boolean;
  private _scrollToFocusedOptionOnUpdate: boolean;
  private dragging: boolean;
  private _openAfterFocus: boolean;
  private _visibleOptions: Array<any>;
  private _focusedOption: any;
  static defaultProps: {
    addLabelText: string;
    arrowRenderer: ({ onMouseDown }: { onMouseDown: any }) => JSX.Element;
    selectModal: null;
    autosize: boolean;
    backspaceRemoves: boolean;
    backspaceToRemoveMessage: string;
    clearable: boolean;
    clearAllText: string;
    clearRenderer: () => JSX.Element;
    clearValueText: string;
    deleteRemoves: boolean;
    delimiter: string;
    disabled: boolean;
    escapeClearsValue: boolean;
    filterOptions: <T = any>(
      options: Array<T>,
      filterValueInput: any,
      excludeOptionsInput: any,
      props: any
    ) => Array<T>;
    ignoreAccents: boolean;
    ignoreCase: boolean;
    inputProps: {};
    isLoading: boolean;
    joinValues: boolean;
    labelKey: string;
    labelKey2: string;
    matchPos: string;
    matchProp: string;
    menuBuffer: number;
    menuRenderer: ({
      focusedOption,
      instancePrefix,
      labelKey,
      onFocus,
      onSelect,
      optionClassName,
      optionComponent,
      optionRenderer,
      options,
      valueArray,
      valueKey,
      onOptionRef,
      values,
    }: {
      focusedOption: any;
      instancePrefix: any;
      labelKey: any;
      onFocus: any;
      onSelect: any;
      optionClassName: any;
      optionComponent: any;
      optionRenderer: IDropdownOptionRenderer;
      options: any;
      valueArray: any;
      valueKey: any;
      onOptionRef: any;
      values: any;
    }) => any;
    multi: boolean;
    noResultsText: string;
    onBlurResetsInput: boolean;
    onCloseResetsInput: boolean;
    openOnFocus: boolean;
    optionComponent: any;
    pageSize: number;
    placeholder: string;
    scrollMenuIntoView: boolean;
    searchable: boolean;
    simpleValue: boolean;
    tabSelectsValue: boolean;
    valueComponent: any;
    valueKey: string;
    renderInBody: boolean;
  };

  constructor(props) {
    super(props);

    this.state = {
      inputValue: '',
      isFocused: false,
      isOpen: false,
      isPseudoFocused: false,
    };

    this._instancePrefix = `react-select-${this.props.instanceId || ++instanceId}-`;

    this.toggleTouchOutsideEvent = this.toggleTouchOutsideEvent.bind(this);
    this.handleTouchOutside = this.handleTouchOutside.bind(this);
    this.focus = this.focus.bind(this);
    this.blurInput = this.blurInput.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleTouchEndClearValue = this.handleTouchEndClearValue.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseDownOnArrow = this.handleMouseDownOnArrow.bind(this);
    this.handleMouseDownOnMenu = this.handleMouseDownOnMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
    this.handleInputFocus = this.handleInputFocus.bind(this);
    this.handleInputBlur = this.handleInputBlur.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleValueClick = this.handleValueClick.bind(this);
    this.handleMenuScroll = this.handleMenuScroll.bind(this);
    this.getValueArray = this.getValueArray.bind(this);
    this.expandValue = this.expandValue.bind(this);
    this.setValue = this.setValue.bind(this);
    this.selectValue = this.selectValue.bind(this);
    this.addValue = this.addValue.bind(this);
    this.popValue = this.popValue.bind(this);
    this.removeValue = this.removeValue.bind(this);
    this.clearValue = this.clearValue.bind(this);
    this.clearModal = this.clearModal.bind(this);
    this.getResetValue = this.getResetValue.bind(this);
    this.focusAdjacentOption = this.focusAdjacentOption.bind(this);
    this.getFocusedOption = this.getFocusedOption.bind(this);
    this.getInputValue = this.getInputValue.bind(this);
    this.selectFocusedOption = this.selectFocusedOption.bind(this);
    this.renderLoading = this.renderLoading.bind(this);
    this.renderValue = this.renderValue.bind(this);
    this.renderInput = this.renderInput.bind(this);
    this.renderClear = this.renderClear.bind(this);
    this.renderArrow = this.renderArrow.bind(this);
    this.renderSelectIcon = this.renderSelectIcon.bind(this);
    this.filterOptions = this.filterOptions.bind(this);
    this.onOptionRef = this.onOptionRef.bind(this);
    this.renderMenu = this.renderMenu.bind(this);
    this.renderHiddenField = this.renderHiddenField.bind(this);
    this.getFocusableOptionIndex = this.getFocusableOptionIndex.bind(this);
    this.getDropdownStyles = this.getDropdownStyles.bind(this);
    this.setDropdownPosition = this.setDropdownPosition.bind(this);
    this.getTargetMetrics = this.getTargetMetrics.bind(this);
    this.renderOuter = this.renderOuter.bind(this);
    this.focusOption = this.focusOption.bind(this);

    this.menuRef = React.createRef<HTMLDivElement>();
    this.focusedRef = React.createRef<Maybe<HTMLElement>>();
    this.menuContainerRef = React.createRef();
    this.wrapperRef = React.createRef();
    this.inputRef = React.createRef();
  }

  componentDidMount() {
    if (this.props.autofocus || this.props.inGrid) {
      this.focus();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.isOpen !== prevState.isOpen) {
      this.toggleTouchOutsideEvent(this.state.isOpen);
      const handler = this.state.isOpen ? this.state.onOpen : this.state.onClose;
      handler?.();
    }

    // focus to the selected option
    this.setDropdownPosition();

    const focusedDOM: Maybe<HTMLElement> = this.focusedRef.current;
    const menuDOM: Maybe<HTMLDivElement> = this.menuRef.current;

    if (menuDOM && focusedDOM && this.state.isOpen && !this.hasScrolledToOption) {
      menuDOM.scrollTop = focusedDOM.offsetTop;
      this.hasScrolledToOption = true;
    } else if (!this.state.isOpen) {
      this.hasScrolledToOption = false;
    }

    if (this._scrollToFocusedOptionOnUpdate && focusedDOM && menuDOM) {
      this._scrollToFocusedOptionOnUpdate = false;

      const focusedRect: Maybe<DOMRect> = focusedDOM?.getBoundingClientRect?.();
      const menuRect: Maybe<DOMRect> = menuDOM?.getBoundingClientRect?.();

      if (focusedRect && menuRect && (focusedRect.bottom > menuRect.bottom || focusedRect.top < menuRect.top)) {
        const offsetTop = focusedDOM?.offsetTop || 0;
        const clientHeight = focusedDOM?.clientHeight || 0;
        const offsetHeight = menuDOM?.offsetHeight || 0;

        menuDOM.scrollTop = offsetTop + clientHeight - offsetHeight;
      }
    }
    if (this.props.scrollMenuIntoView && this.menuContainerRef.current) {
      const menuContainerRect = this.menuContainerRef.current.getBoundingClientRect();
      if (window.innerHeight < menuContainerRect.bottom + this.props.menuBuffer) {
        window.scrollBy(0, menuContainerRect.bottom + this.props.menuBuffer - window.innerHeight);
      }
    }
    if (prevProps.disabled !== this.props.disabled) {
      this.setState({ isFocused: false }); // eslint-disable-line react/no-did-update-set-state
      this.closeMenu();
    }
  }

  componentWillUnmount() {
    document.removeEventListener('touchstart', this.handleTouchOutside);
  }

  toggleTouchOutsideEvent(enabled) {
    if (enabled) {
      document.addEventListener('touchstart', this.handleTouchOutside);
    } else {
      document.removeEventListener('touchstart', this.handleTouchOutside);
    }
  }

  handleTouchOutside(event) {
    // handle touch outside on ios to dismiss menu
    if (
      this.wrapperRef.current &&
      !this.wrapperRef.current.contains(event.target) &&
      this.menuContainerRef.current &&
      !this.menuContainerRef.current.contains(event.target) &&
      this.menuRef.current &&
      !this.menuRef.current.contains(event.target)
    ) {
      this.closeMenu();
    }
  }

  focus() {
    if (!this.inputRef.current) return;
    this.inputRef.current.focus();
  }

  blurInput() {
    if (!this.inputRef.current) return;
    this.inputRef.current.blur();
  }

  handleTouchMove(event) {
    // Set a flag that the view is being dragged
    this.dragging = true;
  }

  handleTouchStart(event) {
    // Set a flag that the view is not being dragged
    this.dragging = false;
  }

  handleTouchEnd(event) {
    // Check if the view is being dragged, In this case
    // we don't want to fire the click event (because the user only wants to scroll)
    if (this.dragging) return;

    // Fire the mouse events
    this.handleMouseDown(event);
  }

  handleTouchEndClearValue(event) {
    // Check if the view is being dragged, In this case
    // we don't want to fire the click event (because the user only wants to scroll)
    if (this.dragging) return;

    // Clear the value
    this.clearValue(event);
  }

  handleMouseDown(event) {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (this.props.disabled || (event.type === 'mousedown' && event.button !== 0)) {
      return;
    }

    if (event.target.tagName === 'INPUT') {
      return;
    }

    // prevent default event handlers
    event.preventDefault();

    // for the non-searchable select, toggle the menu
    if (!this.props.searchable) {
      this.focus();
      return this.setState({
        isOpen: !this.state.isOpen,
      });
    }

    if (this.state.isFocused) {
      // On iOS, we can get into a state where we think the input is focused but it isn't really,
      // since iOS ignores programmatic calls to input.focus() that weren't triggered by a click event.
      // Call focus() again here to be safe.
      this.focus();

      let input = this.inputRef.current;
      if (typeof input.getInput === 'function') {
        // Get the actual DOM input if the ref is an <AutosizeInput /> component
        input = input.getInput();
      }

      // clears the value so that the cursor will be at the end of input when the component re-renders
      input.value = '';

      // if the input is focused, ensure the menu is open
      this.setState({
        isOpen: true,
        isPseudoFocused: false,
      });
    } else {
      // otherwise, focus the input and open the menu
      this._openAfterFocus = true;
      this.focus();
    }
  }

  handleMouseDownOnArrow(event) {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (this.props.disabled || (event.type === 'mousedown' && event.button !== 0)) {
      return;
    }
    // If the menu isn't open, let the event bubble to the main handleMouseDown
    if (!this.state.isOpen) {
      return;
    }
    // prevent default event handlers
    event.stopPropagation();
    event.preventDefault();
    // close the menu
    this.closeMenu();
  }

  handleMouseDownOnMenu(event) {
    // if the event was triggered by a mousedown and not the primary
    // button, or if the component is disabled, ignore it.
    if (this.props.disabled || (event.type === 'mousedown' && event.button !== 0)) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    this._openAfterFocus = true;
    this.focus();
  }

  closeMenu() {
    if (this.props.onCloseResetsInput) {
      this.setState({
        isOpen: false,
        isPseudoFocused: this.state.isFocused && !this.props.multi,
        inputValue: '',
      });
    } else {
      this.setState({
        isOpen: false,
        isPseudoFocused: this.state.isFocused && !this.props.multi,
        inputValue: this.state.inputValue,
      });
    }
    this.hasScrolledToOption = false;
  }

  handleInputFocus(event) {
    if (this.props.disabled) return;
    const isOpen = this.state.isOpen || this._openAfterFocus || this.props.openOnFocus;
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
    this.setState({
      isFocused: true,
      isOpen: isOpen,
    });
    this._openAfterFocus = false;
  }

  handleInputBlur(event) {
    // The check for menu.contains(activeElement) is necessary to prevent IE11's scrollbar from closing the menu in certain contexts.
    if (
      this.menuRef.current &&
      (this.menuRef.current === document.activeElement || this.menuRef.current.contains(document.activeElement))
    ) {
      this.focus();
      return;
    }
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
    const onBlurredState: ISelectState = {
      isFocused: false,
      isOpen: false,
      isPseudoFocused: false,
    };
    if (this.props.onBlurResetsInput) {
      onBlurredState.inputValue = '';
    }
    this.setState(onBlurredState);
  }

  handleInputChange(event) {
    let newInputValue = event.target.value;

    if (this.state.inputValue !== event.target.value && this.props.onInputChange) {
      const nextState = this.props.onInputChange(newInputValue);
      // Note: != used deliberately here to catch undefined and null
      if (nextState && typeof nextState !== 'object') {
        newInputValue = `${nextState}`;
      }
    }

    this.setState({
      isOpen: true,
      isPseudoFocused: false,
      inputValue: newInputValue,
    });
  }

  // eslint-disable-next-line complexity
  handleKeyDown(event) {
    if (this.props.disabled) return;

    if (typeof this.props.onInputKeyDown === 'function') {
      this.props.onInputKeyDown(event);
      if (event.defaultPrevented) {
        return;
      }
    }

    switch (event.keyCode) {
      case 8: // backspace
        if (!this.state.inputValue && !this.props.selectModal && this.props.backspaceRemoves) {
          event.preventDefault();
          this.popValue();
        }
        return;
      case 9: // tab
      case 39: //Treat 'right arrow' as tab.
        if (event.shiftKey || !this.state.isOpen || !this.props.tabSelectsValue) {
          return;
        }
        if (this.props.scanQuery && typeof this.props.scanQuery === 'function') {
          this.props.scanQuery(this.state.inputValue, 9);
        }
        if (this.props.inGrid) {
          event.stopPropagation();
          event.preventDefault();
        }
        this.selectFocusedOption(9);
        return;
      case 13: // enter
        if (!this.state.isOpen) return;
        if (this.props.scanQuery) {
          this.props.scanQuery(this.state.inputValue, 13);
        }
        event.stopPropagation();
        this.selectFocusedOption(13);
        break;
      case 27: // escape
        if (this.state.isOpen) {
          this.closeMenu();
          event.stopPropagation();
        } else if (this.props.clearable && this.props.escapeClearsValue) {
          this.clearValue(event);
          event.stopPropagation();
        }
        break;
      case 38: // up
        this.focusPreviousOption();
        event.stopPropagation();
        break;
      case 40: // down
        this.focusNextOption();
        event.stopPropagation();
        break;
      case 33: // page up
        this.focusPageUpOption();
        event.stopPropagation();
        break;
      case 34: // page down
        this.focusPageDownOption();
        event.stopPropagation();
        break;
      case 35: // end key
        if (event.shiftKey) {
          return;
        }
        this.focusEndOption();
        break;
      case 36: // home key
        if (event.shiftKey) {
          return;
        }
        this.focusStartOption();
        break;
      case 46: // backspace
        if (!this.state.inputValue && this.props.deleteRemoves) {
          event.preventDefault();
          this.popValue();
        }
        return;
      default:
        return;
    }
    event.preventDefault();
  }

  handleValueClick(option, event) {
    if (!this.props.onValueClick) return;
    this.props.onValueClick(option, event);
  }

  handleMenuScroll(event) {
    if (!this.props.onMenuScrollToBottom) return;
    const { target } = event;

    const isScrolledToBottom = target.scrollHeight - target.scrollTop <= target.offsetHeight + 1;

    if (isScrolledToBottom) {
      this.props.onMenuScrollToBottom();
    }
  }

  /**
   * Turns a value into an array from the given options
   * @param value    - the value of the select input
   * @param  nextProps  - optionally specify the nextProps so the returned array uses the latest configuration
   * @returns the value of the select represented in an array
   */
  getValueArray(value: string | number | Array<string | number>, nextProps?: any) {
    // support optionally passing in the `nextProps` so `componentWillReceiveProps` updates will function as expected
    const props = typeof nextProps === 'object' ? nextProps : this.props;
    if (props.multi || Array.isArray(value)) {
      let newValue = value;
      if (typeof newValue === 'string') newValue = newValue.split(props.delimiter);
      if (!Array.isArray(newValue)) {
        if (newValue === null || newValue === undefined) return [];
        newValue = [newValue];
      }
      return newValue.map((valueEntry) => this.expandValue(valueEntry, props)).filter((i) => i);
    }
    const expandedValue = this.expandValue(value, props);
    return expandedValue ? [expandedValue] : [];
  }

  /**
   * Retrieve a value from the given options and valueKey
   * @param value  - the selected value(s)
   * @param props  - the Select component's props (or nextProps)
   */
  expandValue(value: string | number, props: any) {
    const valueType = typeof value;
    if (valueType !== 'string' && valueType !== 'number' && valueType !== 'boolean') return value;
    const { options, valueKey } = props;
    if (!options) return;
    for (let i = 0; i < options.length; i++) {
      if (_get(options[i], valueKey, options[i]) === value) return options[i];
    }
  }

  setValue(value, keyCode?: number) {
    if (this.props.autoBlur) {
      this.blurInput();
    }
    if (!this.props.onChange) return;
    let newValue = value;
    if (this.props.simpleValue && newValue) {
      newValue = this.props.multi
        ? newValue.map((i) => _get(i, this.props.valueKey)).join(this.props.delimiter)
        : _get(newValue, this.props.valueKey);
    }
    this.props.onChange(newValue, keyCode);
  }

  selectValue(value, keyCode?: number) {
    //NOTE: update value in the callback to make sure the input value is empty so that there are no styling issues (Chrome had issue otherwise)
    this.hasScrolledToOption = false;
    if (this.props.multi) {
      this.setState(
        {
          inputValue: '',
          focusedIndex: null,
        },
        () => {
          this.addValue(value);
        }
      );
    } else {
      this.setState(
        {
          isOpen: false,
          inputValue: '',
          isPseudoFocused: this.state.isFocused,
        },
        () => {
          this.setValue(value, keyCode);
        }
      );
    }
  }

  addValue(value) {
    const valueArray = this.getValueArray(this.props.value);
    const visibleOptions = this._visibleOptions.filter((val) => !val.disabled);
    const lastValueIndex = visibleOptions.indexOf(value);
    this.setValue(valueArray.concat(value));
    if (visibleOptions.length - 1 === lastValueIndex) {
      // the last option was selected; focus the second-last one
      this.focusOption(visibleOptions[lastValueIndex - 1]);
    } else if (visibleOptions.length > lastValueIndex) {
      // focus the option below the selected one
      this.focusOption(visibleOptions[lastValueIndex + 1]);
    }
  }

  popValue() {
    const valueArray = this.getValueArray(this.props.value);
    if (!valueArray.length) return;
    if (valueArray[valueArray.length - 1].clearableValue === false) return;
    this.setValue(valueArray.slice(0, valueArray.length - 1));
  }

  removeValue(value) {
    const valueArray = this.getValueArray(this.props.value);
    this.setValue(valueArray.filter((i) => i !== value));
    this.focus();
  }

  clearValue(event) {
    // if the event was triggered by a mousedown and not the primary
    // button, ignore it.
    if (event && event.type === 'mousedown' && event.button !== 0) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();
    this.setValue(this.getResetValue());
    this.setState(
      {
        isOpen: false,
        inputValue: '',
      },
      this.focus
    );
  }

  clearModal() {
    // if the event was triggered by a mousedown and not the primary
    // button, ignore it.
    this.setState({
      isOpen: false,
      inputValue: '',
      isFocused: false,
      isPseudoFocused: false,
    });
    //this.blurInput();
  }

  getResetValue() {
    if (this.props.resetValue !== undefined) {
      return this.props.resetValue;
    } else if (this.props.multi) {
      return [];
    }
    return null;
  }

  focusOption(option) {
    this.setState({
      focusedOption: option,
    });
  }

  focusNextOption = () => {
    this.focusAdjacentOption('next');
  };

  focusPreviousOption = () => {
    this.focusAdjacentOption('previous');
  };

  focusPageUpOption = () => {
    this.focusAdjacentOption('page_up');
  };

  focusPageDownOption = () => {
    this.focusAdjacentOption('page_down');
  };

  focusStartOption = () => {
    this.focusAdjacentOption('start');
  };

  focusEndOption = () => {
    this.focusAdjacentOption('end');
  };

  focusAdjacentOption(dir) {
    let potentialIndex;
    const options = this._visibleOptions
      .map((option, index) => ({ option, index }))
      .filter((option) => !option.option.disabled);
    this._scrollToFocusedOptionOnUpdate = true;
    if (!this.state.isOpen) {
      this.setState({
        isOpen: true,
        inputValue: '',
        focusedOption:
          this._focusedOption || (options.length ? options[dir === 'next' ? 0 : options.length - 1].option : null),
      });
      return;
    }
    if (!options.length) return;
    let focusedIndex = -1;
    for (let i = 0; i < options.length; i++) {
      if (this._focusedOption === options[i].option) {
        focusedIndex = i;
        break;
      }
    }
    if (dir === 'next' && focusedIndex !== -1) {
      focusedIndex = (focusedIndex + 1) % options.length;
    } else if (dir === 'previous') {
      if (focusedIndex > 0) {
        focusedIndex -= 1;
      } else {
        focusedIndex = options.length - 1;
      }
    } else if (dir === 'start') {
      focusedIndex = 0;
    } else if (dir === 'end') {
      focusedIndex = options.length - 1;
    } else if (dir === 'page_up') {
      potentialIndex = focusedIndex - this.props.pageSize;
      if (potentialIndex < 0) {
        focusedIndex = 0;
      } else {
        focusedIndex = potentialIndex;
      }
    } else if (dir === 'page_down') {
      potentialIndex = focusedIndex + this.props.pageSize;
      if (potentialIndex > options.length - 1) {
        focusedIndex = options.length - 1;
      } else {
        focusedIndex = potentialIndex;
      }
    }

    if (focusedIndex === -1) {
      focusedIndex = 0;
    }

    this.setState({
      focusedIndex: options[focusedIndex].index,
      focusedOption: options[focusedIndex].option,
    });
  }

  getFocusedOption() {
    return this._focusedOption;
  }

  getInputValue() {
    return this.state.inputValue;
  }

  selectFocusedOption(keyCode: number) {
    if (this._focusedOption) {
      return this.selectValue(this._focusedOption, keyCode);
    }
  }

  renderLoading() {
    if (!this.props.isLoading) return;
    return (
      <span className="Select-loading-zone" aria-hidden="true">
        <span className="Select-loading" />
      </span>
    );
  }

  renderValue(valueArray, isOpen) {
    const ValueRenderer = this.props.valueRenderer;
    const ValueComponent = this.props.valueComponent;
    if (!valueArray.length) {
      return !this.state.inputValue ? <div className="Select-placeholder">{this.props.placeholder}</div> : null;
    }
    let onClick = this.props.onValueClick ? this.handleValueClick : null;
    if (this.props.multi) {
      return valueArray.map((value, i) => {
        return (
          <ValueComponent
            id={`${this._instancePrefix}-value-${i}`}
            instancePrefix={this._instancePrefix}
            disabled={this.props.disabled || value.clearableValue === false}
            key={`value-${i}-${_get(value, this.props.valueKey)}`}
            onClick={onClick}
            onRemove={this.removeValue}
            value={value}
            data-testid={`${this.props['data-testid']}-value-text`}
          >
            <ValueRenderer option={value} />
            <span className="Select-aria-only">&nbsp;</span>
          </ValueComponent>
        );
      });
    } else if (!this.state.inputValue) {
      if (isOpen) onClick = null;
      return (
        <ValueComponent
          id={`${this._instancePrefix}-value-item`}
          disabled={this.props.disabled}
          instancePrefix={this._instancePrefix}
          onClick={onClick}
          value={valueArray[0]}
          data-testid={`${this.props['data-testid']}-value-text`}
        >
          <ValueRenderer option={valueArray[0]} />
        </ValueComponent>
      );
    }
  }

  renderInput(valueArray, focusedOptionIndex) {
    const className = classNames('Select-input', this.props.inputProps.className);
    const isOpen = this.state.isOpen;

    const ariaOwns = classNames({
      [`${this._instancePrefix}-list`]: isOpen,
      [`${this._instancePrefix}-backspace-remove-message`]:
        this.props.multi && !this.props.disabled && this.state.isFocused && !this.state.inputValue,
    });

    const inputProps = Object.assign({}, this.props.inputProps, {
      role: 'combobox',
      'aria-expanded': `${isOpen}`,
      'aria-owns': ariaOwns,
      'aria-haspopup': `${isOpen}`,
      'aria-activedescendant': isOpen
        ? `${this._instancePrefix}-option-${focusedOptionIndex}`
        : `${this._instancePrefix}-value`,
      'aria-describedby': this.props['aria-describedby'],
      'aria-labelledby': this.props['aria-labelledby'],
      'aria-label': this.props['aria-label'],
      className: className,
      tabIndex: this.props.tabIndex,
      onBlur: this.handleInputBlur,
      onChange: this.handleInputChange,
      onFocus: this.handleInputFocus,
      readOnly: this.props.readOnly,
      ref: this.inputRef,
      value: this.state.inputValue,
      'data-testid': this.props['data-testid'],
    });
    if (this.props.id) inputProps.id = this.props.id;

    if (this.props.inputRenderer) {
      return this.props.inputRenderer(inputProps);
    }

    if (this.props.disabled || !this.props.searchable) {
      const { inputClassName, ...divProps } = this.props.inputProps;

      const ariaOwns = classNames({
        [`${this._instancePrefix}-list`]: isOpen,
      });

      return (
        <div
          {...divProps}
          role="combobox"
          aria-expanded={isOpen}
          aria-owns={ariaOwns}
          aria-activedescendant={
            isOpen ? `${this._instancePrefix}-option-${focusedOptionIndex}` : `${this._instancePrefix}-value`
          }
          className={className}
          tabIndex={this.props.tabIndex || 0}
          onBlur={this.handleInputBlur}
          onFocus={this.handleInputFocus}
          ref={this.inputRef}
          aria-readonly={`${this.props.disabled}`}
          style={{ border: 0, width: 1, display: 'inline-block' }}
        />
      );
    }

    if (this.props.autosize) {
      return <AutosizeInput {...inputProps} minWidth="5" />;
    }
    return (
      <div className={className}>
        <input {...inputProps} />
      </div>
    );
  }

  renderClear() {
    if (
      !this.props.clearable ||
      this.props.value === undefined ||
      this.props.value === null ||
      (this.props.multi && !this.props.value.length) ||
      this.props.disabled ||
      this.props.isLoading ||
      this.props.readOnly
    ) {
      return null;
    }
    const clear = this.props.clearRenderer();

    return (
      <span
        className="Select-clear-zone"
        title={this.props.multi ? this.props.clearAllText : this.props.clearValueText}
        aria-label={this.props.multi ? this.props.clearAllText : this.props.clearValueText}
        onMouseDown={this.clearValue}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEndClearValue}
      >
        {clear}
      </span>
    );
  }

  renderArrow() {
    if (this.props.readOnly || (this.props.remoteFilter && this.props.placeholder)) return;
    const onMouseDown = this.handleMouseDownOnArrow;
    const isOpen = this.state.isOpen;

    const arrow = this.props.arrowRenderer({
      onMouseDown,
      isOpen,
      props: this.props,
    });
    return (
      <span className="Select-arrow-zone" onMouseDown={onMouseDown} data-testid={`${this.props['data-testid']}-arrow`}>
        {arrow}
      </span>
    );
  }

  renderSelectIcon() {
    if (this.props.readOnly || !(this.props.selectModal || this.props.query)) return;

    const noIcon = !this.props.selectModal;

    return (
      <span className={!noIcon ? 'Select-arrow-zone' : undefined} onMouseDown={(e) => e.stopPropagation()}>
        <SelectModal
          onClick={this.clearModal}
          selectModal={this.props.selectModal}
          selectModalData={this.props.selectModalData}
          selectValue={this.selectValue}
          focusOnInput={this.focus}
          {...this.props}
          modalOpen={this.props.workspaceModalOpen}
          setModalOpen={this.props.setWorkspaceModalOpen}
          noIcon={noIcon}
        />
      </span>
    );
  }

  renderCustomIcon() {
    if (this.props.customIcon) {
      const CustomIcon = this.props.customIcon;
      return (
        <span className="Select-arrow-zone" onMouseDown={(e) => e.stopPropagation()}>
          <CustomIcon selectValue={this.selectValue} focusOnInput={this.focus} />
        </span>
      );
    }
  }

  filterOptions(excludeOptions) {
    const filterValue = this.state.inputValue;
    const options = this.props.options || [];

    // Maintain backwards compatibility with boolean attribute
    const filterOptions =
      typeof this.props.filterOptions === 'function' ? this.props.filterOptions : defaultFilterOptions;

    const localFilteredOptions = filterOptions(options, filterValue, excludeOptions, {
      filterOption: this.props.filterOption,
      ignoreAccents: this.props.ignoreAccents,
      ignoreCase: this.props.ignoreCase,
      labelKey: this.props.labelKey,
      matchPos: this.props.matchPos,
      matchProp: this.props.matchProp,
      valueKey: this.props.valueKey,
    });

    // Do not re-filter if the services have done it (Differences in how filtering works result in fractured/missing entries).
    if (this.props.remoteFilter) {
      // If the local filtered options list has one entry (For creating a new record), inject that into the options list for AsyncCreatable support.
      if (localFilteredOptions.length === 1 && localFilteredOptions[0].formattedLabel?.includes('Create option')) {
        return [...options, ...localFilteredOptions];
      }
    } else if (this.props.filterOptions) {
      return localFilteredOptions;
    }

    // Remove already selected options
    return options?.filter((opt) => !excludeOptions?.some((val) => val.id === opt.id));
  }

  onOptionRef(ref, isFocused) {
    if (isFocused) {
      this.focusedRef.current = ref;
    }
  }

  renderMenu(options, valueArray, focusedOption) {
    const ThisMenuRenderer = this.props.menuRenderer;

    if (options && options.length) {
      return (
        <ThisMenuRenderer
          focusedOption={focusedOption}
          focusOption={this.focusOption}
          instancePrefix={this._instancePrefix}
          labelKey={this.props.labelKey}
          onFocus={this.focusOption}
          onSelect={this.selectValue}
          optionClassName={this.props.optionClassName}
          optionComponent={this.props.optionComponent}
          optionRenderer={this.props.optionRenderer}
          options={options}
          selectValue={this.selectValue}
          valueArray={valueArray}
          valueKey={this.props.valueKey}
          onOptionRef={this.onOptionRef}
          data-testid={this.props['data-testid']}
          values={this.context.values}
        />
      );
    } else if (this.props.noResultsText) {
      if (this.props.creatable) {
        return (
          <div
            onClick={() => {
              this.props.onChange(this.props?.textEntered);
            }}
            className="Select-noresults"
          >
            {this.props.noResultsText}
          </div>
        );
      } else {
        return <div className="Select-noresults">{this.props.noResultsText}</div>;
      }
    }
    return null;
  }

  renderHiddenField(valueArray) {
    if (!this.props.name) return;
    if (this.props.joinValues) {
      const value = valueArray.map((i) => stringifyValue(_get(i, this.props.valueKey))).join(this.props.delimiter);
      return (
        <input
          type="hidden"
          name={this.props.name}
          value={value}
          data-testid={`${this.props['data-testid']}-hidden`}
          disabled={this.props.disabled}
        />
      );
    }
    return valueArray.map((item, index) => (
      <input
        key={`hidden.${index}`}
        data-testid={`${this.props['data-testid']}-hidden`}
        type="hidden"
        ref={`value${index}`}
        name={this.props.name}
        value={stringifyValue(_get(item, this.props.valueKey))}
        disabled={this.props.disabled}
      />
    ));
  }

  getFocusableOptionIndex(selectedOption) {
    const options = this._visibleOptions;
    if (!options.length) return null;

    const valueKey = this.props.valueKey;
    const focusedOption = this.state.focusedOption || selectedOption;
    if (focusedOption && !focusedOption.disabled) {
      const focusedOptionIndex = options.findIndex(
        (option) => _get(option, valueKey) === _get(focusedOption, valueKey)
      );
      if (focusedOptionIndex !== -1) {
        return focusedOptionIndex;
      }
    }

    for (let i = 0; i < options.length; i++) {
      if (!options[i].disabled) return i;
    }
    return null;
  }

  getDropdownStyles() {
    const styles: React.CSSProperties = { position: 'fixed', zIndex: 1000010 };

    const { dropdownHPos, dropdownVPos, dropdownTop = 0, dropdownLeft = 0, dropdownRight = 0 } = this.state;
    if (dropdownHPos === 'right') {
      styles.left = `${dropdownLeft}px`;
    } else {
      styles.left = `${dropdownRight - DROPDOWN_WIDTH}px`;
    }
    if (dropdownVPos === 'bottom') {
      const height = window.innerHeight;
      const availableHeight = height - dropdownTop - 34;
      styles.maxHeight = availableHeight < DROPDOWN_MAX_HEIGHT ? availableHeight : DROPDOWN_MAX_HEIGHT;
      styles.top = `${dropdownTop + 34}px`;
    } else {
      this._visibleOptions = this.filterOptions(this.props.multi ? this.getValueArray(this.props.value) : null);
      const options = this._visibleOptions;
      let dropdownHeight, optionsHeight;
      let headerHeight = 0;
      if (this.props.displayFields) {
        headerHeight = Object.keys(this.props.displayFields).length > 1 ? DROPDOWN_OPTION_HEIGHT : 0;
      }
      if (options.length > 0) {
        optionsHeight = options.length > 5 ? DROPDOWN_MAX_HEIGHT : DROPDOWN_OPTION_HEIGHT * options.length;
        dropdownHeight = DROPDOWN_PADDING + headerHeight + optionsHeight;
      } else {
        dropdownHeight = DROPDOWN_OPTION_HEIGHT + DROPDOWN_PADDING;
      }
      styles.top = `${dropdownTop - dropdownHeight}px`;
      styles.maxHeight = dropdownHeight;
    }
    return styles;
  }

  setDropdownPosition() {
    const inputEl = this.getInitialElement();
    if (!inputEl) return;
    const inputMetrics = this.getTargetMetrics(inputEl);
    const hPos = inputMetrics.left + DROPDOWN_WIDTH < window.innerWidth ? 'right' : 'left';
    const height = window.innerHeight;

    const optionsHeight = (this.props.options?.length ?? 0) * DROPDOWN_OPTION_HEIGHT;
    const dropdownRealHeight = optionsHeight <= DROPDOWN_MAX_HEIGHT ? optionsHeight : DROPDOWN_MAX_HEIGHT;
    const vPos = inputMetrics.top + dropdownRealHeight < height ? 'bottom' : 'top';
    if (
      this.state.dropdownHPos !== hPos ||
      this.state.dropdownVPos !== vPos ||
      this.state.dropdownTop !== inputMetrics.top ||
      this.state.dropdownLeft !== inputMetrics.left
    ) {
      this.setState({
        dropdownHPos: hPos,
        dropdownVPos: vPos,
        dropdownTop: inputMetrics.top,
        dropdownLeft: inputMetrics.left,
        dropdownRight: inputMetrics.right,
      });
    }
  }

  getTargetMetrics(targetEl) {
    const rect = targetEl.getBoundingClientRect();
    const pos: { top: number; left: number; width: number; height: number; right?: number; bottom?: number } = {
      top: rect.top,
      left: rect.left,
      width: targetEl.offsetWidth,
      height: targetEl.offsetHeight,
    };
    pos.right = rect.right || pos.left + pos.width;
    pos.bottom = rect.bottom || pos.top + pos.height;
    return pos;
  }

  getInitialElement = () => {
    return this.wrapperRef.current;
  };

  renderOuter(options, valueArray, focusedOption) {
    const menu = this.renderMenu(options, valueArray, focusedOption);
    if (!menu) {
      return null;
    }
    const newStyle = Object.assign({}, this.props.menuContainerStyle, this.getDropdownStyles());

    const content = (
      <div ref={this.menuContainerRef} className="Select-menu-outer" style={newStyle}>
        <div
          ref={this.menuRef}
          role="listbox"
          tabIndex={-1}
          className="Select-menu"
          id={`${this._instancePrefix}-list`}
          style={this.props.menuStyle}
          onScroll={this.handleMenuScroll}
          onMouseDown={this.handleMouseDownOnMenu}
        >
          {menu}
          <OptionCounter
            options={options}
            totalOptionsCount={this.props.totalOptionsCount}
            query={this.props.query}
            setWorkspaceModalOpen={this.props.setWorkspaceModalOpen}
          />
        </div>
      </div>
    );

    if (this.props.renderInBody) {
      return <RenderInBody>{content}</RenderInBody>;
    }

    return content;
  }

  render() {
    const valueArray = this.getValueArray(this.props.value);
    this._visibleOptions = this.filterOptions(this.props.multi ? this.getValueArray(this.props.value) : null);
    const options = this._visibleOptions;
    let isOpen = this.state.isOpen;
    if (this.props.multi && !options.length && valueArray.length && !this.state.inputValue) isOpen = false;
    const focusedOptionIndex = this.getFocusableOptionIndex(valueArray[0]);

    let focusedOption;
    if (focusedOptionIndex !== null) {
      focusedOption = options[focusedOptionIndex];
    } else {
      focusedOption = null;
    }
    this._focusedOption = focusedOption;
    const className = classNames('Select', this.props.className, {
      'Select--multi': this.props.multi,
      'Select--single': !this.props.multi,
      'is-clearable': this.props.clearable,
      'is-disabled': this.props.disabled,
      'is-focused': this.state.isFocused,
      'is-loading': this.props.isLoading,
      'is-open': isOpen,
      'is-pseudo-focused': this.state.isPseudoFocused,
      'is-searchable': this.props.searchable,
      'has-value': valueArray.length,
    });

    let removeMessage: ReactNode | null = null;
    if (
      this.props.multi &&
      !this.props.disabled &&
      valueArray.length &&
      !this.state.inputValue &&
      this.state.isFocused &&
      this.props.backspaceRemoves
    ) {
      removeMessage = (
        <span
          id={`${this._instancePrefix}-backspace-remove-message`}
          className="Select-aria-only"
          aria-live="assertive"
        >
          {this.props.backspaceToRemoveMessage.replace(
            '{label}',
            _get(valueArray[valueArray.length - 1], this.props.labelKey)
          )}
        </span>
      );
    }

    const controlClassName = classNames('Select-control', this.props.hasErrors ? 'Select-input-Error' : null);
    return (
      <div ref={this.wrapperRef} className={className} style={this.props.wrapperStyle}>
        {this.renderHiddenField(valueArray)}
        <div
          className={controlClassName}
          style={this.props.style}
          onKeyDown={this.handleKeyDown}
          onMouseDown={this.handleMouseDown}
          onTouchEnd={this.handleTouchEnd}
          onTouchStart={this.handleTouchStart}
          onTouchMove={this.handleTouchMove}
        >
          <span className="Select-multi-value-wrapper" id={`${this._instancePrefix}-value`}>
            {this.renderValue(valueArray, isOpen)}
            {this.renderInput(valueArray, focusedOptionIndex)}
          </span>
          {removeMessage}
          {this.renderLoading()}
          {this.renderClear()}
          {this.renderArrow()}
          {this.renderSelectIcon()}
          {this.renderCustomIcon()}
          <SidebarIcon inGrid={this.props.inGrid} sidebarForm={this.props.sidebarForm} value={this.props.value} />
        </div>
        {isOpen ? this.renderOuter(options, !this.props.multi ? valueArray : null, focusedOption) : null}
      </div>
    );
  }
}

Select.contextType = ConnectedFormContext;

Select.defaultProps = {
  addLabelText: 'Add "{label}"?',
  arrowRenderer: defaultArrowRenderer,
  selectModal: null,
  autosize: false,
  backspaceRemoves: true,
  backspaceToRemoveMessage: 'Press backspace to remove {label}',
  clearable: true,
  clearAllText: 'Clear all',
  clearRenderer: defaultClearRenderer,
  clearValueText: 'Clear value',
  deleteRemoves: true,
  delimiter: ',',
  disabled: false,
  escapeClearsValue: true,
  filterOptions: defaultFilterOptions,
  ignoreAccents: true,
  ignoreCase: true,
  inputProps: {},
  isLoading: false,
  joinValues: false,
  labelKey: 'label',
  labelKey2: 'id',
  matchPos: 'any',
  matchProp: 'any',
  menuBuffer: 0,
  menuRenderer: MenuRenderer,
  multi: false,
  noResultsText: 'No results found',
  onBlurResetsInput: true,
  onCloseResetsInput: true,
  openOnFocus: false,
  optionComponent: Option,
  pageSize: 5,
  placeholder: 'Select...',
  scrollMenuIntoView: true,
  searchable: true,
  simpleValue: false,
  tabSelectsValue: true,
  valueComponent: Value,
  valueKey: 'value',
  renderInBody: true,
};

const SelectFunctionWrapper = forwardRef<any, Omit<ISelectProps, 'workspaceModalOpen' | 'setWorkspaceModalOpen'>>(
  (props, ref) => {
    const [workspaceModalOpen, setWorkspaceModalOpen] = useState(false);

    const DefaultSelectOptionRenderer: IDropdownOptionRenderer = useCallback(
      ({ option }) => {
        if (option?.formattedLabel) return option.formattedLabel; //Preformatted label, such as for new option creation.

        let label = _get(option, props.labelKey);
        if (!label) label = _get(option, props.labelKey2);

        if (props.rawLabel) return label ?? null; //rawLabel - Do not convert to 'pretty' case, e.g. for allcaps fields.
        return titleCase(label);
      },
      [props.labelKey, props.labelKey2, props.rawLabel]
    );

    return (
      <Select
        {...props}
        ref={ref}
        workspaceModalOpen={workspaceModalOpen}
        setWorkspaceModalOpen={setWorkspaceModalOpen}
        optionRenderer={props.optionRenderer || DefaultSelectOptionRenderer}
        valueRenderer={props.valueRenderer || DefaultSelectOptionRenderer}
      />
    );
  }
);

SelectFunctionWrapper.displayName = 'Select';
//Might be better to export separately - But put this in for compatability.
(SelectFunctionWrapper as any).Async = Async;
(SelectFunctionWrapper as any).AsyncCreatable = AsyncCreatable;
(SelectFunctionWrapper as any).Creatable = Creatable;

export default SelectFunctionWrapper;
